// FieldMap.jsx
import { useState, useContext, useEffect } from "react";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../services/firebaseConfig";
import { useNavigate } from "react-router-dom";

import styles from "./FieldMap.module.css";
import { generateDynamicSummary } from "../../utils/generateDynamicSummary";
import Button from "../../components/common/Button";
import InputArea from "../../components/common/InputArea";
import { UserContext } from "../../context/UserContext";
import { Input } from "../../components/common/Input";
import InfluenceMapModal from "../../components/UserDashboard/InfluenceMapModal";
import FieldMapDraftsCard from "../../components/UserDashboard/FieldMapDraftsCard";
import Modal from "../../components/common/Modal";
import FieldMapEditorHeader from "../../components/UserDashboard/FieldMapEditorHeader";

/* ========= CONSTANTES ========= */

const PROCESS_OPTIONS = [
  { value: "", label: "— não definido —" },
  { value: "emotion", label: "Emoção" },
  { value: "cognition", label: "Cognição" },
  { value: "self", label: "Self" },
  { value: "attention", label: "Atenção" },
];

const PROCESS_COLORS = {
  emotion: "#4caf50",
  cognition: "#2196f3",
  self: "#9c27b0",
  attention: "#f44336",
};

const PROCESS_ORDER = ["emotion", "cognition", "self"];

/* ========= HELPERS ========= */

const sortNodesByProcess = (nodes) => {
  return [...nodes].sort((a, b) => {
    const indexA = PROCESS_ORDER.indexOf(a.processTag);
    const indexB = PROCESS_ORDER.indexOf(b.processTag);

    // Nodes sem processo vão para o final
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    // Ordem por processo
    if (indexA !== indexB) {
      return indexA - indexB;
    }
    return a.text.localeCompare(b.text);
  });
};

const emptyNode = (text = "") => ({
  id: crypto.randomUUID(),
  text,
  domain: "context",
  note: "",
  central: false,
  processTag: "",
  influences: [],
});

/* ========= COMPONENT ========= */

export default function FieldMap() {
  const { user, userData } = useContext(UserContext);
  const navigate = useNavigate();
  const [showInfluenceMap, setShowInfluenceMap] = useState(false);
  const [input, setInput] = useState("");
  const [title, setTitle] = useState("Sem Título");
  const [nodes, setNodes] = useState([]);
  const [connections, setConnections] = useState([]);
  const [editingNode, setEditingNode] = useState(null);
  const [hypothesis, setHypothesis] = useState("");
  const [centralNodeId, setCentralNodeId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showDrafts, setShowDrafts] = useState(false);
  const [draftId, setDraftId] = useState(null); // rascunho ativo
  const [drafts, setDrafts] = useState([]); // lista do usuário
  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [columnLayout, setColumnLayout] = useState({
    context: { row: 0 },
    emotion: { row: 0 },
    cognition: { row: 0 },
    self: { row: 0 },
    attention: { row: 0 },
    values: { row: 0 },
  });

  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!draftId) return;
    setHasUnsavedChanges(true);
  }, [nodes, hypothesis, centralNodeId, connections, columnLayout, title]);

  const loadDraft = (draft) => {
    setDraftId(draft.id);
    setTitle(draft?.title || "Sem Título");
    setNodes(draft?.nodes || []);
    setConnections(draft?.connections || []);
    setColumnLayout(
      draft?.columnLayout || {
        context: { row: 0 },
        emotion: { row: 0 },
        cognition: { row: 0 },
        self: { row: 0 },
        attention: { row: 0 },
        values: { row: 0 },
      }
    );
    setHypothesis(draft?.hypothesis || "");
    setCentralNodeId(draft?.centralNodeId || null);
    setHasUnsavedChanges(false);
  };

  useEffect(() => {
    if (!user) return;

    const loadDrafts = async () => {
      setLoadingDrafts(true);

      const q = query(
        collection(db, "fieldmap"),
        where("userId", "==", user.uid),
        orderBy("updatedAt", "desc"),
        limit(20)
      );

      const snapshot = await getDocs(q);

      const list = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setDrafts(list);

      // 1️⃣ tenta carregar o rascunho ativo
      const activeDraft = list.find((d) => d.isActive === true);

      if (activeDraft) {
        loadDraft(activeDraft);
      } else {
        // 2️⃣ nenhum ativo → editor vazio
        setDraftId(null);
        setNodes([]);
        setHypothesis("");
      }

      setLoadingDrafts(false);
    };

    loadDrafts();
  }, [user]);

  /* ========= NODE ACTIONS ========= */

  const addNode = () => {
    if (!input.trim()) return;
    setNodes((prev) => [...prev, emptyNode(input.trim())]);
    setInput("");
  };

  const updateNode = (id, updates) =>
    setNodes((prev) =>
      prev.map((n) => (n.id === id ? { ...n, ...updates } : n))
    );

  const deleteNode = (nodeId) => {
    setNodes((prev) => prev.filter((n) => n.id !== nodeId));

    setConnections((prev) =>
      prev.filter((c) => c.from !== nodeId && c.to !== nodeId)
    );
  };

  /* ========= MODAL ========= */

  const openEditModal = (node) => {
    setEditingNode({ ...node });
  };

  const closeEditModal = () => {
    setEditingNode(null);
  };

  const confirmEditNode = () => {
    if (!editingNode) return;
    updateNode(editingNode.id, editingNode);
    closeEditModal();
  };

  /* ========= SAVE ========= */

  const handleSave = async () => {
    setError(null);

    if (nodes.length === 0) {
      setError("Adicione ao menos um elemento relevante para salvar.");
      return;
    }

    setSaving(true);

    const validNodeIds = new Set(nodes.map((n) => n.id));

    const sanitizedConnections = connections.filter(
      (c) => validNodeIds.has(c.from) && validNodeIds.has(c.to)
    );
    try {
      await updateDoc(doc(db, "fieldmap", draftId), {
        userId: user.uid,
        title,
        isActive: true,
        status: "draft", // futuro: finalized / archived
        nodes,
        connections: sanitizedConnections,
        columnLayout,
        hypothesis,
        centralNodeId,
        meta: { version: "mapping-v1" },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error(err);
      setError("Erro ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
      setHasUnsavedChanges(false);
    }
  };

  const summary = generateDynamicSummary(nodes);

  const createNewDraft = async () => {
    if (!user) return;

    // desativa o atual
    if (draftId) {
      await updateDoc(doc(db, "fieldmap", draftId), {
        isActive: false,
      });
    }

    const ref = await addDoc(collection(db, "fieldmap"), {
      userId: user.uid,
      title: "Novo FIELDMAP",
      isActive: true,
      status: "draft",
      nodes: [],
      hypothesis: "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    setDraftId(ref.id);
    setNodes([]);
    setHypothesis("");
  };

  const deleteDraft = async (id) => {
    await deleteDoc(doc(db, "fieldmap", id));

    setDrafts((prev) => prev.filter((d) => d.id !== id));

    if (id === draftId) {
      setDraftId(null);
      setNodes([]);
      setHypothesis("");
    }
  };

  {
    !draftId && !loadingDrafts && (
      <div className={styles.emptyState}>
        <p>Nenhum FIELDMAP ativo.</p>
        <Button onClick={createNewDraft}>Criar novo FIELDMAP</Button>
      </div>
    );
  }

  if (!userData?.entitlements?.groupDiscussion?.active) {
    return (
      <Button onClick={() => navigate("/about-group")}>
        Para utilizar a ferramenta é preciso fazer inscrição no grupo
      </Button>
    );
  }

  return (
    <div className={styles.container}>
      <FieldMapEditorHeader
        title={title}
        setTitle={setTitle}
        hasActiveDraft={!!draftId}
        hasUnsavedChanges={hasUnsavedChanges}
        onSave={handleSave}
        onOpenDrafts={() => setShowDrafts(true)}
        onOpenMap={() => setShowInfluenceMap(true)}
        drafts={drafts}
        onCreateNew={() => {
          createNewDraft();
          setShowDrafts(false);
        }}
      />

      {showDrafts && (
        <Modal onClose={() => setShowDrafts(false)}>
          <FieldMapDraftsCard
            drafts={drafts}
            activeDraftId={draftId}
            maxDrafts={10}
            onCreateNew={() => {
              createNewDraft();
              setShowDrafts(false);
            }}
            onOpen={(draft) => {
              loadDraft(draft);
              setShowDrafts(false);
            }}
            onDelete={deleteDraft}
            onRename={async (id, title) => {
              await updateDoc(doc(db, "fieldmap", id), {
                title,
                updatedAt: serverTimestamp(),
              });

              setDrafts((prev) =>
                prev.map((d) => (d.id === id ? { ...d, title } : d))
              );
            }}
          />
        </Modal>
      )}

      {/* ===== INPUT ===== */}
      <div className={styles.card}>
        <div className={styles.inputRow}>
          <Input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addNode()}
            label="Descreva algo que pareça relevante para compreender este caso"
            style={{ flex: 1 }}
          />

          {userData?.entitlements?.groupDiscussion?.active === true ? (
            !draftId ? (
              <div>
                <span className={styles.empty}>Nenhum FIELDMAP ativo </span>
              </div>
            ) : (
              <div>
                <Button onClick={addNode}>+</Button>
              </div>
            )
          ) : (
            <div>
              <Button onClick={() => navigate("/about-group")}>
                Para utilizar a ferramenta é preciso fazer inscrição no grupo
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ===== GRID ===== */}
      <div className={styles.grid}>
        {["context", "process", "values"].map((domain) => (
          <div key={domain} className={styles.card}>
            <h3>
              {domain === "context" && "História & Contexto"}
              {domain === "process" && "Processos"}
              {domain === "values" && "Valores & Anseios"}
            </h3>

            {sortNodesByProcess(nodes)
              .filter((n) => n.domain === domain)
              .map((n) => (
                <div
                  key={n.id}
                  className={styles.node}
                  style={{
                    borderLeft: `4px solid ${
                      n.processTag
                        ? PROCESS_COLORS[n.processTag]
                        : n.central
                        ? "var(--primary)"
                        : "var(--border-light)"
                    }`,
                  }}
                  onClick={() => openEditModal(n)}
                >
                  <p className={styles.nodeText}>{n.text}</p>

                  {n.processTag && (
                    <small
                      style={{
                        color: PROCESS_COLORS[n.processTag],
                        fontSize: "0.75rem",
                        display: "block",
                        marginTop: 4,
                      }}
                    >
                      {
                        PROCESS_OPTIONS.find((p) => p.value === n.processTag)
                          ?.label
                      }
                    </small>
                  )}

                  {n.note && (
                    <small className={styles.nodeNote}>{n.note}</small>
                  )}
                </div>
              ))}
          </div>
        ))}
      </div>

      {/* ===== ELEMENTO CENTRAL DE TRABALHO ===== */}
      <div className={styles.card} style={{ marginTop: 16 }}>
        <h3>Definir elemento central de trabalho</h3>

        <p className={styles.muted} style={{ marginBottom: 8 }}>
          Este elemento pode orientar metáforas, exercícios e foco clínico ao
          longo do processo.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label htmlFor="central-node-select">Elemento central</label>

          <select
            className={styles.select}
            id="central-node-select"
            value={centralNodeId || ""}
            onChange={(e) => {
              const value = e.target.value;
              setCentralNodeId(value || null);
            }}
          >
            <option value="">— Nenhum elemento definido —</option>

            {nodes.map((n) => (
              <option key={n.id} value={n.id}>
                {n.text}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ===== RESUMO ===== */}
      <div className={styles.card} style={{ marginTop: 16 }}>
        <h3>Resumo dinâmico</h3>
        <p className={styles.muted}>{summary}</p>
      </div>

      {/* ===== HIPÓTESE ===== */}
      <div className={styles.card} style={{ marginTop: 16 }}>
        <h3>Hipótese funcional integrada</h3>
        <InputArea
          value={hypothesis}
          onChange={(e) => setHypothesis(e.target.value)}
          label="Quando considero os elementos acima, a hipótese que melhor organiza este caso neste momento é:"
        />
      </div>

      {showInfluenceMap && (
        <InfluenceMapModal
          nodes={sortNodesByProcess(nodes)}
          connections={connections}
          columnLayout={columnLayout}
          onChangeColumnLayout={setColumnLayout}
          onChangeConnections={setConnections}
          onClose={() => setShowInfluenceMap(false)}
        />
      )}

      {/* ===== MODAL ===== */}
      {editingNode && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <div>
                {/* <h3>Editar elemento</h3> */}
                <h3>{editingNode.text}</h3>
              </div>

              <button
                aria-label="Fechar"
                className={styles.closeBtn}
                onClick={() => setEditingNode(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <label>Nome</label>
              <input
                type="text"
                placeholder={editingNode.text}
                value={editingNode.text}
                onChange={(e) => {
                  setEditingNode({ ...editingNode, text: e.target.value });
                }}
              />
              <label>Classificação</label>
              <select
                className={styles.select}
                value={editingNode.domain}
                onChange={(e) =>
                  setEditingNode({
                    ...editingNode,
                    domain: e.target.value,
                  })
                }
              >
                <option value="context">História & Contexto</option>
                <option value="process">Processos</option>
                <option value="values">Valores & Aseios</option>
              </select>

              {editingNode.domain === "process" && (
                <>
                  {" "}
                  <label>Processo psicológico (ACT)</label>
                  <select
                    className={styles.select}
                    value={editingNode.processTag}
                    onChange={(e) =>
                      setEditingNode({
                        ...editingNode,
                        processTag: e.target.value,
                      })
                    }
                  >
                    {PROCESS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </>
              )}

              <label>Hipótese funcional / observações clínicas</label>
              <InputArea
                value={editingNode.note}
                onChange={(e) =>
                  setEditingNode({
                    ...editingNode,
                    note: e.target.value,
                  })
                }
              />

              <div className={styles.modalFooter}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    deleteNode(editingNode.id);
                    closeEditModal();
                  }}
                >
                  Excluir
                </Button>

                <Button onClick={confirmEditNode}>Salvar alterações</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
